import React, { useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  selectedProvincesState,
  selectedPemdaState,
  sidebarisexpandAtom,
  selectedJenisTkdState,
  months,
  startMonthAtom,
  endMonthAtom,
} from "states/recoil";
import {
  Center,
  Spinner,
  Text,
  CardBody,
  CardHeader,
  Heading,
  IconButton,
  Flex,
  Menu,
  MenuList,
  MenuButton,
  MenuItem,
  Button,
} from "@chakra-ui/react";
import { HiChartBar, HiOutlineDownload, HiArrowsExpand } from "react-icons/hi";
import Chart from "react-apexcharts";
import { DownloadImage } from "components/functions/DownloadImage";
import { ExportToExcel } from "components/functions/ExportToExcel";
import { ToggleFullScreen } from "components/functions/ToggleFullscreen";
import { UseTogglePopUp } from "components/functions/UseTogglePopUp";
import { tahunAnggaranAtom } from "states/recoil";

function HorizontalBarChart({ data, xaxisData, titleChart }) {
  //ref to downloadImage
  const chartContainerRef = useRef(null);

  //recoil-state
  const sidebarisexpandGet = useRecoilValue(sidebarisexpandAtom);
  const [selectedProvinces, setSelectedProvinces] = useRecoilState(
    selectedProvincesState
  );
  const [selectedPemda, setSelectedPemda] = useRecoilState(selectedPemdaState);
  const [selectedJenisTKD, setSelectedJenisTKD] = useRecoilState(
    selectedJenisTkdState
  );
  const monthArray = useRecoilValue(months);
  const [selectedStartMonth] = useRecoilState(startMonthAtom);
  const [selectedEndMonth] = useRecoilState(endMonthAtom);

  //local state
  const [isLoading, setIsLoading] = useState(true);
  const [rawData, setRawData] = useState([]);
  const { isOpen, openPopup, closePopup } = UseTogglePopUp();
  const [exportedData, setExportedData] = useState({
    categories: [],
    series: [],
  });

  //chart state
  const [chartDataHorizontalBar, setChartDataHorizontalBar] = useState({
    categories: [],
    series: [],
    raw: { pagu: [], realisasi: [] },
  });

  //get the data
  useEffect(() => {
    setRawData(data);
  }, [data]);

  //Set Chart Data
  useEffect(() => {
    setIsLoading(true);
    if (
      !rawData.length ||
      !selectedProvinces.length ||
      !selectedPemda.length ||
      !selectedJenisTKD.length
    ) {
      setChartDataHorizontalBar({
        categories: [],
        series: [],
        raw: { pagu: [], realisasi: [] },
      });
      return;
    }

    const filteredData = rawData.filter(
      (row) =>
        selectedProvinces.includes(row["NM PROV"]) &&
        selectedPemda.includes(row["NM PEMDA"]) &&
        selectedJenisTKD.includes(row["JENIS TKD"])
    );

    function calculateFilteredRealisasi(row, startMonth, endMonth) {
      let total = 0;
      const startIndex = monthArray.indexOf(startMonth);
      const endIndex = monthArray.indexOf(endMonth);

      for (let i = startIndex; i <= endIndex; i++) {
        const monthNumber = (i + 1).toString().padStart(2, "0");
        const key = `REALISASI ${monthNumber}`;
        total += parseFloat(row[key] || 0);
      }
      return total;
    }

    const grouped = {};
    filteredData.forEach((row) => {
      const subjenis = row["SUB JENIS TKD"];
      if (!subjenis) return;

      if (!grouped[subjenis]) {
        grouped[subjenis] = {
          "PAGU ALOKASI": 0,
          REALISASI: 0,
        };
      }

      grouped[subjenis]["PAGU ALOKASI"] += parseFloat(row["PAGU ALOKASI"] || 0);
      grouped[subjenis]["REALISASI"] += calculateFilteredRealisasi(
        row,
        selectedStartMonth,
        selectedEndMonth
      );
    });

    const categories = Object.keys(grouped);
    const fullPagu = [];
    const fullRealisasi = [];
    const paguData = [];
    const realisasiData = [];

    categories.forEach((key) => {
      if (!grouped[key]) return;
      const pagu = grouped[key]["PAGU ALOKASI"];
      if (pagu === 0) {
        // Skip entries where pagu is 0
        return;
      }
      const real = grouped[key]["REALISASI"];
      fullPagu.push(pagu);
      fullRealisasi.push(real);
      paguData.push(pagu);
      realisasiData.push(real);
    });

    setChartDataHorizontalBar({
      categories,
      series: [
        {
          name: "REALISASI",
          data: realisasiData.map((val, idx) => ({
            x: categories[idx],
            y: val,
            goals: [
              {
                name: "PAGU",
                value: paguData[idx],
                strokeHeight: 7,
                strokeColor: "#005599",
              },
            ],
          })),
        },
      ],
      raw: {
        pagu: fullPagu,
        realisasi: fullRealisasi,
      },
    });

    setExportedData({
      categories,
      series: [
        { name: "Pagu", data: paguData },
        { name: "Realisasi", data: realisasiData },
        {
          name: "Progress",
          data: paguData.map((paguValue, index) => {
            const realisasiValue = realisasiData[index];
            return paguValue ? (realisasiValue / paguValue) * 100 : 0;
          }),
        },
      ],
    });

    setIsLoading(false);
  }, [
    rawData,
    selectedProvinces,
    selectedPemda,
    selectedJenisTKD,
    selectedEndMonth,
    selectedStartMonth,
    tahunAnggaranAtom,
  ]);

  //set chart options
  const chartOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "70%",
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        const goals =
          opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex]
            .goals;
        if (goals && goals.length) {
          const paguValue = goals[0].value;
          if (paguValue && paguValue !== 0) {
            const percentage = (val / paguValue) * 100;
            return `${percentage.toFixed(2)}%`;
          }
        }
        return "";
      },
      style: {
        colors: ["#858585"],
        fontSize: "8px",
        fontWeight: "normal",
      },
      offsetY: 0, // move label vertically
      background: {
        enabled: true,
        foreColor: "#000", // text color (can override `style.colors`)
        color: "#ffffff", // background color
        padding: 4,
        borderRadius: 3,
        opacity: 0.1,
      },
    },

    tooltip: {
      shared: false,
      intersect: true,
      y: {
        formatter: (val, { dataPointIndex, w }) => {
          const realisasis = w.config.customData?.realisasisubjenis ?? [];
          const pagus = w.config.customData?.pagusubjenis ?? [];

          const originalRealisasi = realisasis[dataPointIndex] ?? val;
          const originalPagu = pagus[dataPointIndex] ?? null;

          const formattedRealisasi = originalRealisasi.toLocaleString("de-DE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });

          let percentageText = "";
          if (originalPagu) {
            const percentage = (originalRealisasi / originalPagu) * 100;
            percentageText = ` (${percentage.toFixed(2)}%)`;
          }

          return formattedRealisasi + percentageText;
        },
      },
      title: {
        formatter: (seriesName, { dataPointIndex, w }) => {
          const goals = w.config.series[0].data[dataPointIndex].goals || [];
          const paguGoal = goals.find((g) => g.name === "PAGU");

          const originalPagu =
            w.config.customData?.pagusubjenis?.[dataPointIndex] ??
            (paguGoal ? paguGoal.value : null);

          const formattedPagu =
            originalPagu?.toLocaleString("de-DE", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) ?? "-";

          return `${seriesName} (Pagu: ${formattedPagu})`;
        },
      },
    },
    xaxis: {
      labels: {
        show: false,
      },
      axisBorder: {
        show: true,
      },
      axisTicks: {
        show: true,
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: "10px",
          minWidth: "890px",
          maxWidth: "1000px",
          whiteSpace: "normal",
        },
      },
    },
    legend: { show: true, position: "bottom" },
    colors: ["#00E396"],
  };

  return (
    <>
      <CardHeader
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex align="center" gap={4}>
          <IconButton
            icon={
              <HiChartBar
                style={{
                  transform: "scaleX(-1)",
                  transform: "rotate(90deg)",
                }}
              />
            }
            aria-label="chart"
            size="sm"
          />
          <Heading size="sm">{titleChart}</Heading>
        </Flex>
        <Flex gap={4}>
          <IconButton
            icon={<HiArrowsExpand />}
            aria-label="export"
            size="sm"
            onClick={openPopup}
          />
          <Menu>
            <MenuButton as={Button} size="sm">
              <HiOutlineDownload />
            </MenuButton>
            <MenuList
              maxHeight="300px"
              overflowY="auto"
              minWidth="250px"
              zIndex={3}
            >
              <MenuItem
                onClick={() =>
                  DownloadImage(chartContainerRef.current, titleChart)
                }
                title={titleChart}
              >
                Unduh sebagai gambar (.PNG)
              </MenuItem>
              <MenuItem
                onClick={() =>
                  ExportToExcel(exportedData, `${titleChart}.xlsx`, false)
                }
              >
                Unduh sebagai spreadsheet (.xlsx)
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </CardHeader>
      <CardBody ref={chartContainerRef}>
        {isLoading ? (
          <Center w="100%" h="500px">
            <Spinner size="xl" color="blue.500" />
          </Center>
        ) : chartDataHorizontalBar.categories.length === 0 ? (
          <Center w="100%" h="500px">
            <Text>Tidak ada data yang ditampilkan.</Text>
          </Center>
        ) : (
          <Chart
            options={chartOptions}
            series={chartDataHorizontalBar.series}
            type="bar"
            height={1000}
            width="100%"
          />
        )}
      </CardBody>

      {isOpen && (
        <ToggleFullScreen onClose={closePopup} rawData={rawData}>
          {/* Render the expanded chart here */}
          <div
            style={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            {isLoading ? (
              <Center w="100%" h="500px">
                <Spinner size="xl" color="blue.500" />
              </Center>
            ) : rawData.length === 0 ? (
              <Center w="100%" h="500px">
                <Text>Tidak ada data yang ditampilkan.</Text>
              </Center>
            ) : (
              <Center w="100%" h="100%">
                <Chart
                  options={chartOptions}
                  series={chartDataHorizontalBar.series}
                  type="bar"
                  height={1000}
                  width="400%"
                />
              </Center>
            )}
          </div>
        </ToggleFullScreen>
      )}
    </>
  );
}

export default HorizontalBarChart;
